import React from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { usePageMeta } from "@/lib/use-page-meta";

// EDIT THESE TWO STRINGS WHEN YOU HAVE YOUR FINAL BUSINESS DETAILS
const APP_NAME = "Plainly";
const CONTACT_EMAIL = "office@myplainly.com";
const EFFECTIVE_DATE = "May 22, 2026";

export default function PrivacyPolicy() {
  usePageMeta({
    title: "Privacy Policy",
    description:
      "How Plainly handles your Google Analytics data, what we store, who our sub-processors are, and your GDPR / CCPA rights.",
    path: "/privacy",
  });
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-16">
        <div className="tag-label text-[#E27D60]">Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">Privacy Policy</h1>
        <p className="text-sm text-[#1E392A]/55 mt-2">Effective {EFFECTIVE_DATE}</p>

        <article
          className="mt-10 space-y-8 text-[#1E392A]/85 leading-relaxed"
          data-testid="privacy-policy-content"
        >
          <section>
            <p>
              {APP_NAME} ("we", "us", "our") provides an analytics-translation service that
              reads your Google Analytics data and explains it in plain English with AI.
              This Privacy Policy describes what we collect, how we use it, who we share it
              with, and the rights you have over your data.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">1. Information we collect</h2>
            <p>We collect only what we need to run the service.</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li>
                <b>Account info.</b> When you sign in with Google we receive your name,
                email address, and profile picture from Google.
              </li>
              <li>
                <b>Google Analytics data.</b> If you connect Google Analytics, we request
                read-only access (<code>analytics.readonly</code>) and fetch metrics for
                the GA4 property you select and the date ranges you choose (sessions,
                users, pageviews, conversions, bounce rate, engagement rate, and date).
                We do not request, store, or have access to your raw events,
                personally-identifying visitor information, or any GA configuration
                outside what's needed for the report.
              </li>
              <li>
                <b>Uploaded CSVs.</b> If you upload a Google Analytics CSV export, the
                aggregated metrics in that file are stored alongside your analysis history.
              </li>
              <li>
                <b>Analyses & AI output.</b> The plain-English summaries the AI generates
                are stored with your account so you can view your history.
              </li>
              <li>
                <b>Payment info.</b> We use Stripe to process payments. We never see your
                full card number — Stripe handles that directly. We store a transaction
                record (amount, plan, status) for billing history.
              </li>
              <li>
                <b>Email preferences.</b> If you opt into the Monday-morning email, we
                track when the last one was sent and whether the feature is enabled.
              </li>
              <li>
                <b>Branding (Agency plan only).</b> Your company name, logo URL, brand
                color, and tagline are stored to white-label shared reports.
              </li>
              <li>
                <b>Share stats.</b> When you create a share link, we count how many times
                the public page is viewed and how many times its referral CTA is clicked.
                We don't store who viewed it.
              </li>
              <li>
                <b>Technical logs.</b> Standard server logs (IP, timestamp, request path)
                are retained for security and debugging.
              </li>
              <li>
                <b>Analytics (only with your consent).</b> If you click "Accept analytics"
                on the cookie banner, we load Google Analytics 4 to count pageviews and
                see which referrers send traffic. IPs are anonymised. We never serve ads
                or use this data for cross-site personalisation. You can revoke consent
                any time via the "Cookies" link in the footer.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">2. How we use your data</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>To authenticate you and run the analyses you request.</li>
              <li>To send AI-generated, plain-English explanations of your GA metrics back to you.</li>
              <li>To send the optional Monday-morning email (only if you turn it on).</li>
              <li>To process payments and manage your subscription.</li>
              <li>To make shareable read-only versions of analyses you choose to share.</li>
              <li>To improve product quality (aggregated, never identifying any one user).</li>
            </ul>
            <p className="mt-3">
              We do <b>not</b> sell your data. We do <b>not</b> use your Google Analytics
              data to train AI models. We do <b>not</b> serve ads.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">3. Limited use of Google user data</h2>
            <p>
              {APP_NAME}'s use of information received from Google APIs adheres to the{" "}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Google API Services User Data Policy
              </a>
              , including the Limited Use requirements. Specifically:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li>We only use Google Analytics data to provide the analytics-translation features described in this policy.</li>
              <li>We never transfer Google user data to any third party except as needed to provide or improve the user-facing features (e.g. sending AI requests through Anthropic's API; see Section 5).</li>
              <li>We never use Google user data for serving ads, including retargeting or personalized advertising.</li>
              <li>We never allow humans to read your Google user data unless we have your affirmative agreement for a specific support case, it's necessary for security purposes, or it's required by law.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">4. How long we keep your data</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>Account, analyses, and email preferences: until you delete your account.</li>
              <li>Google Analytics OAuth tokens: until you disconnect or delete your account.</li>
              <li>Payment records: up to 7 years (as required by financial regulations).</li>
              <li>Server logs: typically 30 days.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">5. Sub-processors we use</h2>
            <p>We share data only with the vendors that power the service:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li><b>Google</b> — OAuth + Google Analytics Data API (your GA data passes through Google's APIs to us).</li>
              <li><b>Anthropic</b> — AI provider for plain-English summaries. The aggregated metrics for your selected periods are sent to Anthropic's Claude API; raw event data is not sent.</li>
              <li><b>Stripe</b> — payment processing.</li>
              <li><b>Resend</b> — transactional email (the Monday-morning report and account emails).</li>
              <li><b>Google Analytics 4</b> — only loaded if you click "Accept analytics" in the cookie banner. Used for aggregate visitor counts.</li>
              <li><b>MongoDB Atlas / our managed database</b> — storage of account, analyses, and share data.</li>
              <li><b>Cloud hosting provider</b> — runs the application servers.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">6. Your rights</h2>
            <p>You can:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li>Disconnect Google Analytics any time (Dashboard → Disconnect). We immediately revoke and delete the OAuth tokens.</li>
              <li>Revoke any individual share link.</li>
              <li>Turn the Monday-morning email off any time.</li>
              <li>Export or delete your data — email us at <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a> and we'll respond within 30 days.</li>
              <li>If you're an EU/UK resident, you have additional rights under GDPR (access, rectification, erasure, restriction, portability, objection). Contact us to exercise them.</li>
              <li>If you're a California resident, you have rights under CCPA (right to know, delete, and opt-out of sale — we don't sell data).</li>
            </ul>
            <p className="mt-3">
              You can also revoke {APP_NAME}'s access to your Google account directly at{" "}
              <a
                href="https://myaccount.google.com/permissions"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                myaccount.google.com/permissions
              </a>.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">7. Security</h2>
            <p>
              We use TLS for all data in transit. OAuth tokens and payment info are encrypted
              at rest. Access to production systems is limited to authorised engineers. No
              system is perfectly secure; if we ever discover a breach affecting you, we'll
              notify you without undue delay.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">8. Children's privacy</h2>
            <p>
              {APP_NAME} is not intended for children under 13 (or under 16 in the EEA). We
              don't knowingly collect data from children.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">9. Changes to this policy</h2>
            <p>
              If we make material changes, we'll update the effective date and notify you by
              email or in-app before they take effect.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">10. Contact us</h2>
            <p>
              Questions, requests, or complaints: <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>.
            </p>
          </section>
        </article>

        <div className="mt-12 pt-6 border-t border-black/5 flex items-center justify-between text-sm">
          <Link to="/" className="underline text-[#1E392A]/70" data-testid="privacy-home-link">← Back to home</Link>
          <Link to="/terms" className="underline text-[#1E392A]/70" data-testid="privacy-terms-link">Terms of Service →</Link>
        </div>
      </main>
    </div>
  );
}
